import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Clock, AlertTriangle, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Exito() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "invalid" });
      return;
    }
    let tries = 0;
    let timer;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/payments/status/${sessionId}`);
        if (r.ok) {
          const b = await r.json();
          if (b.payment_status === "paid" && b.status === "active") {
            if (!cancelled) setState({ phase: "active", registrationId: b.registration_id });
            return;
          }
        }
      } catch {
        // reintenta
      }
      tries += 1;
      if (tries < 15) {
        timer = setTimeout(poll, 2000);
      } else if (!cancelled) {
        setState({ phase: "pending" });
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <main data-testid="page-exito" className="min-h-screen blueprint-grid flex items-center">
      <div className="mx-auto w-full max-w-[1100px] px-6 lg:px-12 py-40">
        {state.phase === "checking" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="exito-checking">
            <span className="font-mono text-[11px] tracking-[0.4em] text-glow/80 block mb-8">PAGO RECIBIDO</span>
            <h1 className="font-display font-800 uppercase text-5xl sm:text-7xl tracking-tight text-offwhite mb-10">
              Confirmando
              <br />
              <span className="font-cormorant normal-case font-medium text-metal">tu suscripción…</span>
            </h1>
            <div className="flex items-center gap-4 text-mist font-mono text-xs tracking-[0.25em]">
              <Loader2 className="h-4 w-4 animate-spin" />
              UN MOMENTO, ESTAMOS CONFIRMANDO EL PAGO
            </div>
          </motion.div>
        )}

        {state.phase === "active" && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="max-w-2xl"
            data-testid="exito-active"
          >
            <div className="flex items-center gap-3 mb-10">
              <ShieldCheck className="h-5 w-5 text-glow" strokeWidth={1.5} />
              <span className="font-mono text-[11px] tracking-[0.4em] text-glow/80">PAGO CONFIRMADO</span>
            </div>
            <h1 className="font-display font-800 uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl text-offwhite mb-8">
              Suscripción
              <br />
              <span className="font-cormorant normal-case font-medium text-metal">activa.</span>
            </h1>
            <p className="text-mist leading-relaxed mb-10 max-w-lg">
              Tu cuota mensual está activa. Desde ahora, cuando tu comercio tenga una incidencia física, solo
              tendrás que comunicarla: Aprea gestiona el resto.
            </p>
            <div className="border border-silver/15 bg-panel/40 p-7 mb-12">
              <span className="font-mono text-[10px] tracking-[0.35em] text-steel block mb-3">
                IDENTIFICADOR DE TU REGISTRO
              </span>
              <span className="font-mono text-2xl md:text-3xl text-metal" data-testid="exito-registration-id">
                {state.registrationId}
              </span>
              <div className="mt-5 pt-5 border-t border-silver/10 font-mono text-[10px] tracking-[0.2em] text-steel">
                ESTADO — <span className="text-glow/90">ACTIVO</span>
              </div>
            </div>
            <Link
              to="/"
              data-testid="exito-home"
              className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] bg-silver text-night px-8 py-4 hover:bg-offwhite transition-colors duration-300"
            >
              VOLVER AL INICIO
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>
        )}

        {state.phase === "pending" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl" data-testid="exito-pending">
            <div className="flex items-center gap-3 mb-10">
              <Clock className="h-5 w-5 text-glow" strokeWidth={1.5} />
              <span className="font-mono text-[11px] tracking-[0.4em] text-glow/80">PAGO EN PROCESO</span>
            </div>
            <h1 className="font-display font-800 uppercase text-5xl sm:text-6xl tracking-tight text-offwhite mb-8">
              Estamos
              <br />
              <span className="font-cormorant normal-case font-medium text-metal">confirmándolo.</span>
            </h1>
            <p className="text-mist leading-relaxed max-w-lg mb-12">
              El pago se está procesando. Tu suscripción quedará activa en cuanto se confirme; no necesitas
              hacer nada más.
            </p>
            <Link
              to="/"
              className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] border border-silver/30 text-silver hover:border-silver hover:text-offwhite px-8 py-4 transition-colors duration-300"
            >
              VOLVER AL INICIO
            </Link>
          </motion.div>
        )}

        {state.phase === "invalid" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl" data-testid="exito-invalid">
            <div className="flex items-center gap-3 mb-10">
              <AlertTriangle className="h-5 w-5 text-glow" strokeWidth={1.5} />
              <span className="font-mono text-[11px] tracking-[0.4em] text-glow/80">SESIÓN NO ENCONTRADA</span>
            </div>
            <h1 className="font-display font-800 uppercase text-5xl sm:text-6xl tracking-tight text-offwhite mb-8">
              No encontramos
              <br />
              <span className="font-cormorant normal-case font-medium text-metal">esta sesión.</span>
            </h1>
            <p className="text-mist leading-relaxed max-w-lg mb-12">
              Si acabas de completar el pago, tu registro se activará automáticamente. Si prefieres revisarlo,
              vuelve al alta.
            </p>
            <Link
              to="/alta"
              className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.25em] border border-silver/30 text-silver hover:border-silver hover:text-offwhite px-8 py-4 transition-colors duration-300"
            >
              VOLVER AL ALTA
            </Link>
          </motion.div>
        )}
      </div>
    </main>
  );
}
